/**
 * `v-arrastrable`: permite mover un popup de Framework7 arrastrándolo por su barra superior, para
 * que no tape lo que hay debajo (p. ej. la previsualización de un ticket mientras se revisan los
 * datos que la IA ha leído en el formulario).
 *
 * Cómo funciona: los popups de F7 en escritorio se centran con `left/top: 50%` y márgenes
 * negativos, y la animación de abrir/cerrar va en `transform`. Para mover el popup se tocan SOLO
 * los márgenes, así no se pelea con la transición de F7. Al cerrarse vuelve al centro.
 *
 * Los límites se calculan con la posición REAL del popup (`getBoundingClientRect`), no suponiendo
 * que está centrado: en móvil F7 lo pinta a pantalla completa (left/top 0) y con la suposición del
 * centro se podía sacar la barra fuera de la ventana sin forma de recuperarla.
 *
 * Uso: `<f7-popup v-arrastrable ...>`. El asa es el `.navbar` del popup (o el propio popup si no
 * tiene); los enlaces, botones y campos dentro del asa siguen funcionando (no inician arrastre).
 * Funciona con ratón y con dedo: pointer events, y touch events donde no los haya (Safari iOS
 * anterior a 13.4).
 */
const ARRASTRABLE = Symbol('arrastrable');
/** Franja del popup que siempre queda dentro de la ventana, para poder volver a cogerlo. */
const FRANJA_VISIBLE = 48;

function esInteractivo(objetivo) {
    return !!(objetivo && objetivo.closest && objetivo.closest('a, button, input, select, textarea, .link, .button, [contenteditable]'));
}

function preparar(el) {
    if (!el || el[ARRASTRABLE]) return;
    const asa = el.querySelector('.navbar') || el;
    if (!asa) return;

    let arrastre = null;

    const empezar = (objetivo, clientX, clientY) => {
        if (esInteractivo(objetivo)) return false;
        const estilo = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        arrastre = {
            x: clientX,
            y: clientY,
            marginLeft: parseFloat(estilo.marginLeft) || 0,
            marginTop: parseFloat(estilo.marginTop) || 0,
            // Dónde está de verdad el popup al empezar: los límites se miden desde aquí.
            left: rect.left,
            top: rect.top,
            ancho: rect.width,
            alto: rect.height
        };
        el.classList.add('popup-arrastrando');
        return true;
    };

    const mover = (clientX, clientY) => {
        if (!arrastre) return;
        const dx = clientX - arrastre.x;
        const dy = clientY - arrastre.y;
        // Posición a la que iría la esquina del popup, acotada para que quede siempre una franja
        // dentro de la ventana (por la derecha, por la izquierda y por abajo) y la barra no suba
        // por encima del borde superior, que es por donde se coge.
        const minLeft = -arrastre.ancho + FRANJA_VISIBLE;
        const maxLeft = window.innerWidth - FRANJA_VISIBLE;
        const minTop = 0;
        const maxTop = window.innerHeight - FRANJA_VISIBLE;
        const left = Math.min(maxLeft, Math.max(minLeft, arrastre.left + dx));
        const top = Math.min(maxTop, Math.max(minTop, arrastre.top + dy));
        // El desplazamiento aplicado es el que de verdad se ha permitido, trasladado a los márgenes.
        el.style.marginLeft = (arrastre.marginLeft + (left - arrastre.left)) + 'px';
        el.style.marginTop = (arrastre.marginTop + (top - arrastre.top)) + 'px';
    };

    const terminar = () => {
        if (!arrastre) return;
        arrastre = null;
        el.classList.remove('popup-arrastrando');
    };

    // --- pointer events (ratón, dedo y lápiz en todo navegador moderno) ---
    const onPointerDown = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        if (!empezar(e.target, e.clientX, e.clientY)) return;
        try { asa.setPointerCapture(e.pointerId); } catch (err) { /* sin captura, sigue valiendo */ }
        e.preventDefault();
    };
    const onPointerMove = (e) => mover(e.clientX, e.clientY);
    const onPointerUp = (e) => {
        if (!arrastre) return;
        try { asa.releasePointerCapture(e.pointerId); } catch (err) { /* nada */ }
        terminar();
    };

    // --- touch events: solo donde no hay pointer events (Safari iOS < 13.4) ---
    const onTouchStart = (e) => {
        const t = e.touches && e.touches[0];
        if (!t || !empezar(e.target, t.clientX, t.clientY)) return;
        e.preventDefault();
    };
    const onTouchMove = (e) => {
        const t = e.touches && e.touches[0];
        if (!arrastre || !t) return;
        mover(t.clientX, t.clientY);
        e.preventDefault();
    };
    const onTouchEnd = () => terminar();

    // Al cerrarse vuelve al centro: la siguiente apertura no debe aparecer donde se dejó.
    const onCerrado = () => {
        el.style.marginLeft = '';
        el.style.marginTop = '';
    };

    const conPointer = typeof window !== 'undefined' && 'PointerEvent' in window;

    asa.style.cursor = 'move';
    asa.style.touchAction = 'none';
    if (conPointer) {
        asa.addEventListener('pointerdown', onPointerDown);
        asa.addEventListener('pointermove', onPointerMove);
        asa.addEventListener('pointerup', onPointerUp);
        asa.addEventListener('pointercancel', onPointerUp);
    } else {
        asa.addEventListener('touchstart', onTouchStart, { passive: false });
        asa.addEventListener('touchmove', onTouchMove, { passive: false });
        asa.addEventListener('touchend', onTouchEnd);
        asa.addEventListener('touchcancel', onTouchEnd);
    }
    el.addEventListener('popup:closed', onCerrado);

    el[ARRASTRABLE] = () => {
        if (conPointer) {
            asa.removeEventListener('pointerdown', onPointerDown);
            asa.removeEventListener('pointermove', onPointerMove);
            asa.removeEventListener('pointerup', onPointerUp);
            asa.removeEventListener('pointercancel', onPointerUp);
        } else {
            asa.removeEventListener('touchstart', onTouchStart);
            asa.removeEventListener('touchmove', onTouchMove);
            asa.removeEventListener('touchend', onTouchEnd);
            asa.removeEventListener('touchcancel', onTouchEnd);
        }
        el.removeEventListener('popup:closed', onCerrado);
        asa.style.cursor = '';
        asa.style.touchAction = '';
        delete el[ARRASTRABLE];
    };
}

export default {
    inserted(el) {
        preparar(el);
    },
    componentUpdated(el) {
        // El navbar del popup puede montarse después (v-if dentro del popup): se reintenta.
        if (!el[ARRASTRABLE]) preparar(el);
    },
    unbind(el) {
        if (el[ARRASTRABLE]) el[ARRASTRABLE]();
    }
};
